import ToolBox from '../ToolBox';
import Template from "ui/Template";
import {i18n} from "ui/i18n";
import {EVENT_UPDATE_TIMELINE} from "ui/canvas/Canvas";
import {EVENT_FILL_FRAME} from "ui/canvas/CanvasLayer";
import DragController from "ui/helpers/DragController";
import ElementsPicker from "ui/editor/dialogs/elements-picker/ElementsPicker";
import LayerProps from "ui/editor/dialogs/layer-props/LayerProps";
import RepeatSetup from "ui/editor/dialogs/repeat-setup/RepeatSetup";
import layerHeadView from './html/layer-head.handlebars';
import layerFramesView from './html/layer-frames.handlebars';
import timelineLayersView from './html/timeline-layers.handlebars';
import {CurrentUser} from "ui/CurrentUser";
import PopupMenu from "ui/elements/popup-menu/PopupMenu";

/**
 * @type {Template}
 */
let layerHeadTemplate = null;

/**
 * @type {Template}
 */
let layerFramesTemplate = null;

/**
 * Timeline and Layers Bar element
 */
class TimelineLayers extends ToolBox {
	/**
	 * @param {SceneEditor} editor
	 * @param {Object} [options]
	 */
	constructor(editor, options = {}) {
		super(Object.assign({
			title: i18n('title.timeline')
		}, options));

		let frames = [];
		for (let a = 0, l = this.scene.canvas.getTotalFrames(); a < l; a++) {
			frames[a] = a + 1;
		}

		/**
		 * @type {Array.<number>}
		 * @private
		 */
		this._framesSequence = frames;

		/**
		 * @type {SceneEditor}
		 * @private
		 */
		this._editor = editor;

		/**
		 * @type {{frame: number, layer: CanvasLayer}}
		 * @private
		 */
		this._selected = {
			frame: -1,
			layer: null
		};

		/**
		 * @type {{from: number, to: number}}
		 * @private
		 */
		this._shadow = {
			from: -1,
			to: -1
		};

		layerHeadTemplate = new Template(layerHeadView);
		layerFramesTemplate = new Template(layerFramesView);

		this.setBodyElement(
			new Template(timelineLayersView)
				.createElement({
					frames
				})
		);

		this._els.scaleFrames = this.element.querySelectorAll('.editor-timeline__time-cell');
		this._els.timeScale = this.element.querySelector('.editor-timeline__time-scale');
		this._els.layersCont = this.element.querySelector('.editor-timeline__layers');
		this._els.bodyCont = this.element.querySelector('.editor-timeline__body');
		this._els.cursor = this.element.querySelector('.editor-timeline__cursor');

		/**
		 * @type {Map<string, HTMLElement>}
		 */
		this._els.buttons = (() => {
			let out = new Map();
			for (let el of this.element.querySelectorAll('.editor-timeline__button')) {
				out.set(el.getAttribute('data-role'), el);
			}
			return out;
		})();

		/**
		 * List of the layer element
		 *
		 * @type {Map<CanvasLayer, T_TimelineLayerView>}
		 * @private
		 */
		this._layers = new Map();

		/**
		 * @type {Array.<T_TimelineLayerView>}
		 * @private
		 */
		this._layersList = [];

		this._handlers = {
			render: this.render.bind(this),
			renderLazy: this.renderLazy.bind(this),
			renderFrames: this._renderFrames.bind(this),
			renderLayers: this._renderLayers.bind(this)
		};

		this._timers = {
			renderLazy: 0
		};

		let canvas = this.scene.canvas;
		canvas.events.addListener(EVENT_UPDATE_TIMELINE, this._handlers.renderLazy);

		this._initTimeScale();
		this._initLayersBody();
		this._initButtons();
	}

	/**
	 * Makes render in the next js frame
	 *
	 * @returns {TimelineLayers}
	 */
	renderLazy() {
		if (this._timers.renderLazy > 0) {
			clearTimeout(this._timers.renderLazy);
		}
		this._timers.renderLazy = setTimeout(this._handlers.render, 0);
		return this;
	}

	/**
	 * Renders timeline: update style of the selected elements and so on
	 *
	 * @returns {void}
	 */
	render() {
		this._renderLayers();
		this._renderCursor();

		let frames = this._els.scaleFrames,
			frame = this.scene.canvas.getCurrentFrame();

		// select path to the shadow frame starting from current frame
		let shadowFrame = this.scene.canvas.getShadowFrame(),
			shadowRange = this._shadow;
		if (shadowFrame !== null) {
			for (let f = 0, l = this.scene.canvas.getTotalFrames(); f < l; f++) {
				let inPrevRange = f >= shadowRange.from && f <= shadowRange.to;
				if (f >= shadowFrame && f < frame) {
					// frame in shadow
					if (!inPrevRange) {
						frames[f].classList.add('editor-timeline__time-cell_shadow');
					}
				} else {
					// frame outside shadow
					if (inPrevRange) {
						frames[f].classList.remove('editor-timeline__time-cell_shadow');
					}
				}
			}
			shadowRange.from = shadowFrame;
			shadowRange.to = frame - 1;
		} else {
			// cancel all frame in shadow
			if (shadowRange.from !== -1) {
				for (let f = shadowRange.from; f <= shadowRange.to; f++) {
					frames[f].classList.remove('editor-timeline__time-cell_shadow');
				}
				shadowRange.from = shadowRange.to = -1;
			}
		}

		// select current layer head
		let prevLayer = this._selected.layer,
			layer = this.scene.canvas.getCurrentLayer();
		if (prevLayer !== layer) {
			if (prevLayer !== null) {
				let view = this._layers.get(prevLayer);
				view.head.classList.remove('editor-layer-head_selected');
				view.framesCont.classList.remove('editor-layer-frames_selected');
			}
			this._selected.layer = layer;
			if (layer !== null) {
				let view = this._layers.get(layer);
				view.head.classList.add('editor-layer-head_selected');
				view.framesCont.classList.add('editor-layer-frames_selected');
			}
		}

		// update current layer frames
		this._renderFrames();
	}

	/**
	 * @param {boolean} forceUpdate
	 * @private
	 */
	_renderCursor(forceUpdate = false) {
		const frames = this._els.scaleFrames;
		const prevFrame = this._selected.frame;
		const frame = this.scene.canvas.getCurrentFrame();

		if (frame !== prevFrame || forceUpdate) {
			if (prevFrame >= 0) {
				frames[this._selected.frame].classList.remove('editor-timeline__time-cell_selected');
			}
			this._selected.frame = frame;
			let frmEl = frames[frame];
			frmEl.classList.add('editor-timeline__time-cell_selected');
			this._els.cursor.style.left = frmEl.offsetLeft
				+ (this._els.timeScale.offsetLeft - this._els.bodyCont.scrollLeft)
				+ 'px';
		}
	}

	/**
	 * Renders list of layers
	 *
	 * @private
	 */
	_renderLayers() {
		let layers = this.scene.canvas.getLayers(),
			oldViews = this._layersList.slice(),
			needReorder = false,
			newViews = [];

		for (let layer of layers) {
			let view = this._layers.get(layer);
			if (!view) {
				view = this._createLayerView(layer);
				this._layers.set(layer, view);
				this._els.layersCont.appendChild(view.head);
				this._els.bodyCont.appendChild(view.framesCont);
				needReorder = true;
			} else {
				this._updateLayerView(layer);
			}
			newViews.push(view);
		}
		let toRemove = oldViews.filter(view => newViews.indexOf(view) === -1);
		if (toRemove.length > 0) {
			for (let view of toRemove) {
				if (this._selected.layer === view.layer) {
					this._selected.layer = null;
				}
				this._layers.delete(view.layer);
				this._els.layersCont.removeChild(view.head);
				this._els.bodyCont.removeChild(view.framesCont);
				needReorder = true;
			}
		}
		if (!needReorder) {
			needReorder = newViews.slice().map(view => view.layer.getId()).join(',')
				!== oldViews.slice().map(view => view.layer.getId()).join(',');
		}

		if (needReorder) {
			let headCont = document.createDocumentFragment(),
				framesCont = document.createDocumentFragment();
			for (let view of newViews) {
				headCont.appendChild(view.head);
				framesCont.appendChild(view.framesCont);
			}
			this._els.layersCont.appendChild(headCont);
			this._els.bodyCont.appendChild(framesCont);
		}
		this._layersList = newViews;
	}

	/**
	 * Renders layers frames (changed only)
	 *
	 * @private
	 * @returns {void}
	 */
	_renderFrames() {
		const selection = this.scene.canvas.getFramesSelection();

		for (let view of this._layersList) {
			if (view.layer.version !== view.lastVersion) {
				view.lastVersion = view.layer.version;
				let statuses = view.layer.getFramesStatuses(),
					frames = this._layers.get(view.layer).frames;
				for (let i = 0, l = statuses.length; i < l; i++) {
					frames[i].setAttribute('data-status', statuses[i]);
				}
			}
		}

		if (this._selected.layer) {
			let frames = this._layers.get(this._selected.layer).frames,
				curFrame = this._selected.frame;
			for (let i = 0, l = frames.length; i < l; i++) {
				let cl = frames[i].classList;
				cl.toggle(
					'editor-layer-frames__cell_highlight',
					i >= selection.from && i <= selection.to
				);
				cl.toggle(
					'editor-layer-frames__cell_highlight_last',
					i === selection.to
				);
				cl.toggle(
					'editor-layer-frames__cell_selected',
					i === curFrame
				);
			}
		}
		this._updateButtons();
	}

	/**
	 * Creates layer view element
	 *
	 * @param {CanvasLayer} layer
	 * @returns {T_TimelineLayerView}
	 * @private
	 */
	_createLayerView(layer) {
		let props = {
			id: layer.getId(),
			title: layer.getTitle(),
			framesSequence: this._framesSequence,
		};
		let head = layerHeadTemplate.createElement(props),
			body = layerFramesTemplate.createElement(props);

		let view = {
			layer,
			head,
			title: head.querySelector('.editor-layer-head__title'),
			eyeButton: head.querySelector('.editor-layer-head__button_eye'),
			version: layer.version,
			framesCont: body,
			frames: Array.from(body.querySelectorAll('.editor-layer-frames__cell'))
		};

		if (layer.isReference) {
			body.classList.add('editor-layer-frames_reference');
		}

		head.addEventListener('click', () => {
			this.scene.canvas.selectLayer(layer);
		});

		head.addEventListener('dblclick', async () => {
			await new LayerProps({layer}).getResultPromise();
			this._updateLayerView(layer);
			this.scene.canvas.redrawArea();
		});

		view.eyeButton.addEventListener('dblclick', (e) => {
			e.stopPropagation();
			e.preventDefault();
		});

		view.eyeButton.addEventListener('click', (e) => {
			e.stopPropagation();
			e.preventDefault();
			layer.toggleVisibility();
			this._updateLayerView(layer);
		});

		layer.events.addListener(EVENT_FILL_FRAME, this._handlers.renderFrames);

		let dragPoint = head.querySelector('.editor-layer-head__drag-point'),
			startIndex = 0, step, items;

		new DragController({
			element: dragPoint,
			start: () => {
				items = [];
				for (let i = 0, l = this._layersList.length; i < l; i++) {
					items.push(this._layersList[i]);
					if (this._layersList[i] === view) {
						startIndex = i;
					}
				}
				this._toggleLayersDrag(true);
				head.classList.add('editor-layer-head_drag');
				step = Math.round(dragPoint.getBoundingClientRect().height) + 1;
			},
			move: (dx, dy) => {
				let newIndex = Math.min(this._layersList.length, Math.max(0, startIndex + Math.round(dy / step)));
				let curIndex = items.indexOf(view);
				if (newIndex !== curIndex) {
					if (newIndex === this._layersList.length) {
						items.push(view);
					} else {
						items.splice(newIndex, 0, view);
					}
					if (curIndex < newIndex) {
						items.splice(curIndex, 1);
					} else {
						items.splice(curIndex + 1, 1);
					}
				}
				let y = 0;
				for (let view of items) {
					view.head.style.top = y + 'px';
					y += step;
				}
			},
			finish: () => {
				if (items.length > 1) {
					this.scene.canvas.rearrangeLayersOrder(items.map(view => view.layer));
				}
				head.classList.remove('editor-layer-head_drag');
				this._toggleLayersDrag(false);
			}
		});
		return view;
	}

	/**
	 * Updates layer view
	 *
	 * @param {CanvasLayer} layer
	 * @returns {void}
	 * @private
	 */
	_updateLayerView(layer) {
		let view = this._layers.get(layer);
		if (view) {
			let props = layer.getProps();
			view.title.textContent = props.title;
			view.eyeButton.classList.toggle('editor-layer-head__button_unset', !props.isVisible);
		}
	}

	/**
	 * Enables/disables drag & drop mode for layers list
	 *
	 * @param {boolean} flag
	 * @private
	 */
	_toggleLayersDrag(flag) {
		if (flag) {
			for (let view of this._layersList) {
				view.head.style.top = view.head.offsetTop + 'px';
			}
			this._els.layersCont.classList.add('editor-timeline__layers_drag');
		} else {
			let order = [];
			for (let view of this._layersList) {
				order.push({view, y: view.head.offsetTop});
				view.head.style.top = '';
			}
			order.sort((a, b) => a.y - b.y);
			let newList = [];

			for (let {view} of order) {
				newList.push(view);
			}
			this._els.layersCont.classList.remove('editor-timeline__layers_drag');
		}
	}

	/**
	 * Initializes timescale element
	 *
	 * @private
	 * @returns {void}
	 */
	_initTimeScale() {
		let step;
		new DragController({
			element: this._els.timeScale,
			start: (x) => {
				x += this._els.timeScale.scrollLeft;
				let len = this._els.scaleFrames.length - 1;
				step = this._els.scaleFrames[len].offsetLeft / len;
				this.scene.canvas.setFrame(~~(x / step));
			},
			move: (dx, dy, x) => {
				x += this._els.timeScale.scrollLeft;
				this.scene.canvas.setFrame(~~((x + dx) / step));
			}
		});
	}

	/**
	 * Initializes layers container
	 *
	 * @private
	 * @returns {void}
	 */
	_initLayersBody() {
		let stepX, stepY = 0,
			selectRangeMode = false,
			dragFrameFrom, dragFrameTo,
			framesCount = this.scene.canvas.getTotalFrames() - 1;

		const select = (dx, dy, x, y) => {
			if (stepY === 0) {
				return;
			}
			let layersList = this._layersList;
			let frameInd = Math.max(0, Math.min(framesCount, ~~((x + dx) / stepX)));
			if (selectRangeMode) {
				let from = Math.min(dragFrameFrom, dragFrameTo, frameInd);
				dragFrameTo = Math.max(dragFrameFrom, dragFrameTo, frameInd);
				dragFrameFrom = from;
				this.scene.canvas.setFrame(frameInd, dragFrameFrom, dragFrameTo);
			} else {
				let layerInd = Math.max(0, Math.min(layersList.length - 1, ~~((y + dy) / stepY)));
				this.scene.canvas.selectLayer(layersList[layerInd].layer);
				this.scene.canvas.setFrame(frameInd);
			}
		};

		new DragController({
			element: this._els.bodyCont,
			start: (x, y, e) => {
				let layersList = this._layersList;
				if (layersList.length === 0) {
					stepY = 0;
					return;
				}

				x += this._els.bodyCont.scrollLeft;
				y += this._els.bodyCont.scrollTop;

				let len = this._els.scaleFrames.length - 1;
				stepX = this._els.scaleFrames[len].offsetLeft / len;
				len = layersList.length - 1;
				let head = layersList[len].head;
				stepY = (head.offsetTop + head.offsetHeight) / (len + 1);

				let layerInd = Math.max(0, Math.min(layersList.length - 1, ~~(y / stepY))),
					frameInd = Math.max(0, Math.min(framesCount, ~~(x / stepX)));

				selectRangeMode = true;

				if (e.shiftKey && dragFrameFrom && dragFrameTo) {
					let from = Math.min(dragFrameFrom, dragFrameTo, frameInd);
					dragFrameTo = Math.max(dragFrameFrom, dragFrameTo, frameInd);
					dragFrameFrom = from;
				} else {
					dragFrameTo = dragFrameFrom = frameInd;
				}

				this.scene.canvas.selectLayer(layersList[layerInd].layer);
				this.scene.canvas.setFrame(frameInd);
				this._els.bodyCont.classList.add('editor-timeline__body_drag');

				select(0, 0, x, y);
			},
			move: (dx, dy, x, y) => {
				x += this._els.bodyCont.scrollLeft;
				y += this._els.bodyCont.scrollTop;
				select(dx, dy, x, y);
			},
			finish: () => {
				this._els.bodyCont.classList.remove('editor-timeline__body_drag');
			}
		});

		let scrollArea = null,
			scrollTime = 0;

		this._els.layersCont.addEventListener('scroll', () => {
			let time = Date.now();
			if (time - scrollTime < 100 && scrollArea === 2) {
				// prevent echo scroll call
				// this is because with sync scrolls between two areas and smooth scroll fires many events
				return;
			}
			scrollTime = time;
			scrollArea = 1;
			this._els.bodyCont.scrollTop = this._els.layersCont.scrollTop;
		});

		this._els.bodyCont.addEventListener('scroll', () => {
			let time = Date.now();
			if (time - scrollTime < 100 && scrollArea === 1) {
				// prevent echo scroll call
				// this is because with sync scrolls between two areas and smooth scroll fires many events
				return;
			}
			scrollTime = time;
			scrollArea = 2;
			this._els.layersCont.scrollTop = this._els.bodyCont.scrollTop;
			this._els.timeScale.scrollLeft = this._els.bodyCont.scrollLeft;
			this._renderCursor(true);
		});
	}

	/**
	 * Initializes buttons
	 *
	 * @private
	 * @returns {void}
	 */
	_initButtons() {
		for (let [role, el] of this._els.buttons.entries()) {
			el.addEventListener('click', () => {
				switch (role) {
					case 'add-layer':
						this.scene.canvas.addLayer({});
						break;
					case 'delete-layer':
						this.scene.canvas.removeLayer(this.scene.canvas.getCurrentLayer());
						break;
					case 'merge-layer':
						this.scene.canvas.mergeCurrentLayerWithNext();
						break;
					case 'import-layer':
						this._importScene(el).then();
						break;
					case 'duplicate-layer':
						void this.scene.canvas.duplicateCurrentLayer();
						break;
					case 'set-keyframe':
						this.scene.canvas.makeKeyFrame();
						break;
					case 'set-blank-keyframe':
						this.scene.canvas.makeBlankKeyFrame();
						break;
					case 'insert-frame':
						this.scene.canvas.insertFrame();
						break;
					case 'delete-frame':
						this.scene.canvas.removeFrame();
						break;
					case 'invert-frames':
						this.scene.canvas.invertFrames();
						break;
					case 'repeat-frames':
						new RepeatSetup().getResultPromise().then(props => {
							if (!props) {
								return;
							}

							this.scene.canvas.repeatFrames(props.times, props.isReflected);
						});
						break;
					case 'shadow-frame':
						this.scene.canvas.enableShadowFrame(!this.scene.canvas.isShadowFrameEnabled());
						this._updateButtons();
						break;
					case 'select-to-end':
						this.scene.canvas.enableSelectToEnd(!this.scene.canvas.isSelectToEndEnabled());
						this._updateButtons();
						break;
					case 'toggle-visibility':
						let visible = 0;
						let layers = this.scene.canvas.getLayers();
						layers.forEach(l => visible += l.isVisible() ? 1 : -1);
						layers.forEach(l => l.toggleVisibility(visible < 0));
						this._renderLayers();
						break;
				}
			});
		}
	}

	/**
	 * Updates buttons states
	 *
	 * @private
	 * @returns {void}
	 */
	_updateButtons() {
		let layer = this.scene.canvas.getCurrentLayer(),
			isOperable = layer.isOperable,
			status = layer.getCurrentFrameStatus(),
			selectedFrames = this.scene.canvas.getFramesSelection(),
			canInsertFrame = layer.canInsertFrame();

		for (let [role, el] of this._els.buttons.entries()) {
			switch (role) {
				case 'set-keyframe':
					el.disabled = !isOperable || status === 1 || status === 3;
					break;
				case 'set-blank-keyframe':
					el.disabled = !isOperable || status === 3;
					break;
				case 'insert-frame':
					el.disabled = !isOperable || !canInsertFrame || selectedFrames.from > -1;
					break;
				case 'invert-frames':
					el.disabled = !isOperable || selectedFrames.from === -1;
					break;
				case 'delete-frame':
					el.disabled = !isOperable;
					break;
				case 'delete-layer':
					el.disabled = this.scene.canvas.getLayers().length <= 1;
					break;
				case 'repeat-frames':
					el.disabled = !isOperable;
					break;
				case 'shadow-frame':
					el.disabled = !isOperable;
					el.classList.toggle('editor-timeline__button_checked', this.scene.canvas.isShadowFrameEnabled());
					break;
				case 'select-to-end':
					el.classList.toggle('editor-timeline__button_checked', this.scene.canvas.isSelectToEndEnabled());
					break;
				case 'merge-layer':
					let layers = this.scene.canvas.getLayers(),
						layerIndex = layers.indexOf(layer),
						nextLayer = layers[layerIndex + 1];

					el.disabled = !isOperable || (nextLayer && !nextLayer.isOperable);
					break;

			}
			el.classList.toggle('button_disabled', el.disabled || false);
		}
	}

	/**
	 * @private
	 * @param {HTMLElement} el
	 * @returns {Promise<void>}
	 */
	async _importScene(el) {
		const popup = new PopupMenu({
			target: el,
			buttons: [
				{
					caption: i18n('buttons.import-from-file'),
					icon: '/images/icons.svg#icon-svg-open',
					role: 'file',
				},
				{
					caption: i18n('buttons.import-from-lib'),
					icon: '/images/icons.svg#icon-svg-import',
					role: 'lib',
				}
			]
		});
		const role = await popup.getResult();
		if (role === 'lib') {
			const lib = new ElementsPicker({
				pickerMode: true,
				onSelect: (data, name) => {
					lib.destroy();
					this.scene.canvas.addLayer({data}, true);
				}
			});
		} else if (role === 'file') {
			this._editor.storage.import().then();
		}
	}
}

export default TimelineLayers;
